import { IMAGES } from "assets/images";
import { FilesUploader } from "components";
import { MediaDirectoryKeys } from "constants/enums";
import { t } from "i18next";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { FormField, MediaFilePickerAlternative, Modal } from "ui";

import {
  useSharingsSchedulesSettings,
  useUpdateSharingSchedulesSettings
} from "../../../apis";
import {
  SharingsSectionStyleFormData,
  SharingsSectionStyleModalProps
} from "./SharingsSectionStyleModal.types";

import styles from "./SharingsSectionStyleModal.module.scss";

const SHARINGS_SECTION_STYLE_FORM_ID = "SHARINGS_SECTION_STYLE_FORM_ID";

const SectionStyleModal = ({
  isOpen,
  onClose
}: SharingsSectionStyleModalProps) => {
  const { mode, logo, background, isLoading } = useSharingsSchedulesSettings();
  const {
    mutate: mutateUpdateSharingsSchedulesSettings,
    isLoading: isLoadingUpdateSharingsSchedulesSettings
  } = useUpdateSharingSchedulesSettings({
    onSuccess: () => {
      onClose();
      toast.success(t("Section Style was changed successfully"));
    }
  });

  const { control, setValue, handleSubmit } =
    useForm<SharingsSectionStyleFormData>({
      values: {
        logo: logo ?? null,
        background: background ?? null
      }
    });

  const onSubmit = (formData: SharingsSectionStyleFormData) => {
    if (mode) {
      mutateUpdateSharingsSchedulesSettings({
        mode,
        logoId: formData.logo?.id ?? null,
        backgroundId: formData.background?.id ?? null
      });
    }
  };

  return (
    <Modal
      formId={SHARINGS_SECTION_STYLE_FORM_ID}
      isOpen={isOpen}
      title="Products to Share Today images"
      isLoading={isLoadingUpdateSharingsSchedulesSettings}
      okText="Save"
      onClose={onClose}
      onOk={handleSubmit(onSubmit)}
      className={styles.modal}
      contentClassName={styles.modal_content}
    >
      <div className={styles.modal_text}>
        {t(
          "Banner and background image for the “Products to Share Today” section in the app."
        )}
      </div>

      <div className={styles.modal_images}>
        <Controller
          control={control}
          name="background"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FormField
              label={"Background image or GIF"}
              errorMessage={error?.message}
            >
              <FilesUploader
                onSuccessUploading={onChange}
                uploadDirectoryKey={MediaDirectoryKeys.ProductSharing}
              >
                {(uploaderProps) => (
                  <MediaFilePickerAlternative
                    {...uploaderProps}
                    value={value}
                    placeholder={IMAGES.SharingsSectionDefaultBackground}
                    size={{ width: "full", height: 215 }}
                    isLoading={isLoading}
                    onDeleteMediaFile={() => onChange(null)}
                  />
                )}
              </FilesUploader>
            </FormField>
          )}
        />

        <Controller
          control={control}
          name="logo"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FormField
              label={"Banner image or GIF"}
              errorMessage={error?.message}
            >
              <FilesUploader
                onSuccessUploading={(media) => {
                  setValue("logo", null);
                  onChange(media);
                }}
                uploadDirectoryKey={MediaDirectoryKeys.ProductSharing}
              >
                {(uploaderProps) => (
                  <MediaFilePickerAlternative
                    {...uploaderProps}
                    value={value}
                    placeholder={IMAGES.SharingsSectionDefaultHeader}
                    isLoading={isLoading}
                    size={{ width: "full", height: 215 }}
                    customMaxFileSizeInMB={2}
                    onDeleteMediaFile={() => onChange(null)}
                  />
                )}
              </FilesUploader>
            </FormField>
          )}
        />
      </div>
    </Modal>
  );
};

export default SectionStyleModal;
